import * as React from 'react';
import type {JSX} from 'react';

import {Wash} from './Dialog';
import {useDialogIsolation} from './hooks/useDialogIsolation';
import Layer from './Layer';

type SharedProps = {
  // Whether Escape closes the dialog. A dialog that cannot escape needs a
  // visible control that finishes it.
  canEscape?: boolean;
  // Whether a click on the backdrop closes the dialog. It follows `canEscape`
  // unless it is given.
  closeOnBackdropClick?: boolean;
};

type OwnProps = {
  children: React.ReactNode;
  open: boolean;
  onClose?: () => void;
};

type Props = OwnProps & SharedProps;

// How many holds the content of a modal dialog has on it. See
// `useHoldModalOpen`.
const HoldContext = React.createContext<React.RefObject<number> | null>(null);

/**
 * Keeps the modal dialog around the calling component open while `hold` is
 * true: Escape and a click on the backdrop do nothing. Use it while the
 * dialog cannot be cancelled, such as while it saves with its Cancel button
 * disabled. It works in `ModalLayer`, `ModalOpener` and the dialogs of
 * `Stage`.
 */
export const useHoldModalOpen = (hold: boolean) => {
  const holds = React.useContext(HoldContext);
  // A layout effect, so that the hold is in place before the next event.
  React.useLayoutEffect(() => {
    if (!hold || !holds) {
      return;
    }
    holds.current++;
    return () => {
      holds.current--;
    };
  }, [hold, holds]);
};

/**
 * A modal dialog on its backdrop, isolated from the page for as long as it is
 * mounted (see `useDialogIsolation`). Mount it only while the dialog is open.
 * Leave out `onBackdropClick` or `onEscape` for a dialog that must not close
 * that way.
 */
export const IsolatedModal = ({
  children,
  onBackdropClick,
  onEscape,
}: {
  children: React.ReactNode;
  onBackdropClick?: () => void;
  onEscape?: () => void;
}) => {
  const holds = React.useRef(0);
  const unlessHeld = (close?: () => void) =>
    close &&
    (() => {
      if (!holds.current) {
        close();
      }
    });
  const ref = useDialogIsolation({onEscape: unlessHeld(onEscape)});
  return (
    <Layer>
      <Wash onClick={unlessHeld(onBackdropClick)} ref={ref}>
        <HoldContext.Provider value={holds}>{children}</HoldContext.Provider>
      </Wash>
    </Layer>
  );
};

const ModalLayer = ({
  canEscape = true,
  children,
  closeOnBackdropClick = canEscape,
  onClose,
  open,
}: Props) => {
  if (!open) {
    return null;
  }
  return (
    <IsolatedModal
      onBackdropClick={closeOnBackdropClick ? onClose : undefined}
      onEscape={canEscape ? onClose : undefined}
    >
      {children}
    </IsolatedModal>
  );
};

export default ModalLayer;

type OpenerProps = {
  children: (renderPropArgs: {handleOpen: () => void}) => JSX.Element;
  renderModal: (renderPropArgs: {handleClose: () => void}) => JSX.Element;
};

export const ModalOpener = ({
  children,
  renderModal,
  ...rest
}: OpenerProps & SharedProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const handleOpen = React.useCallback(() => setIsOpen(true), []);
  const handleClose = React.useCallback(() => setIsOpen(false), []);

  return (
    <>
      {children({handleOpen})}
      <ModalLayer {...rest} onClose={handleClose} open={isOpen}>
        {(isOpen && renderModal({handleClose})) || <div />}
      </ModalLayer>
    </>
  );
};
